import { useEffect, useRef, useState } from "react";

import type { Client, ClientState, PusherConfig } from "~/lib/matrix";
import { registerApnsToken, takeLaunchPushTap } from "~/lib/platform";

import { useAndroidPushRegistration } from "~/hooks/useAndroidPushRegistration";

const SYGNAL_GATEWAY_URL = "https://sygnal.matrix.nurion.tech/_matrix/push/v1/notify";
const IOS_APP_ID = "com.nurion.chat.ios";

// Transient-acquisition / registration retry budget: initial attempt, then these
// delays. Permission denial (a null token) is NOT transient and is not retried.
const RETRY_DELAYS_MS = [1_000, 5_000, 30_000] as const;

export interface RegisteredIosPusher {
  appId: string;
  pushkey: string;
}

// Secret-safe teardown: the rejection reason is a network error, never token /
// key / account material, so it is safe to surface while failing loud.
export async function unregisterIosPusher(
  client: Client,
  pusher: RegisteredIosPusher,
): Promise<void> {
  try {
    await client.bindings.unregisterPusher(pusher.appId, pusher.pushkey);
  } catch (err) {
    console.warn("usePushRegistration: pusher unregister failed", err);
  }
}

// `mutable-content` + `content-available` wake the Notification Service Extension
// so it can replace the generic `loc-key` alert with decrypted text.
export function buildIosPusher(pushkey: string): PusherConfig {
  return {
    appId: IOS_APP_ID,
    pushkey,
    gatewayUrl: SYGNAL_GATEWAY_URL,
    appDisplayName: "Nurion Chat",
    deviceDisplayName: "iOS",
    defaultPayload: {
      aps: {
        "mutable-content": 1,
        "content-available": 1,
        alert: { "loc-key": "Notification" },
      },
    },
  };
}

/**
 * True from the first sync of a signed-in session until sign-out, so a
 * reconnect (synced → reconnecting → synced) doesn't re-prompt or re-register.
 */
export function useSignedInLatch(state: ClientState, userId: string | null): boolean {
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    if (userId === null) setSignedIn(false);
    else if (state === "synced") setSignedIn(true);
  }, [state, userId]);
  return signedIn;
}

export interface UsePushRegistrationArgs {
  /** Signed-in Matrix client, or null when unsigned / unauthenticated. */
  client: Client | null;
  /** Latched sign-in signal — drives the registration lifecycle. */
  signedIn: boolean;
  /** Route a buffered notification tap to its room. */
  onNotificationTap: (roomId: string) => void;
  /** Register a teardown that runs while the client is still authenticated. */
  registerSignOutTeardown?: (teardown: () => Promise<void> | void) => () => void;
}

type AttemptOutcome = "registered" | "denied" | "network" | "cancelled";

/**
 * Registers an iOS APNs pusher against the Nurion Sygnal gateway with a bounded,
 * in-memory retry around transient failures, rotates on token change, and tears
 * the pusher down on sign-out. No token / key / account / message data is logged.
 */
export function usePushRegistration({
  client,
  signedIn,
  onNotificationTap,
  registerSignOutTeardown,
}: UsePushRegistrationArgs): void {
  useAndroidPushRegistration({
    client,
    signedIn,
    onNotificationTap,
    registerSignOutTeardown,
  });
  // Session-scoped flags. Invalidity is keyed on the live client, never a counter,
  // so a benign re-render must not cancel in-flight work.
  const activeClientRef = useRef<Client | null>(null);
  const signingOutRef = useRef(false);
  const inFlightRef = useRef(false);
  const rearmRequestedRef = useRef(false);
  const registeredRef = useRef<RegisteredIosPusher | null>(null);
  // A pusher whose rotation is pending: if sign-out interrupts the cycle between
  // registering the replacement and removing this one, the teardown removes it.
  const rotatingOutRef = useRef<RegisteredIosPusher | null>(null);
  // Only phase sign-out may await: the registration settle (which records the
  // registered pusher), never the preceding token acquisition.
  const registrationRef = useRef<Promise<unknown> | null>(null);
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // --- Sign-out teardown + foreground/online re-arm: bound once per client. ---
  // Kept in its own effect so a same-client re-render (signedIn toggle, StrictMode)
  // neither loses the teardown nor leaks listeners; only a real client change or
  // unmount tears them down.
  useEffect(() => {
    if (!client) return;
    activeClientRef.current = client;
    signingOutRef.current = false;
    inFlightRef.current = false;
    rearmRequestedRef.current = false;
    registeredRef.current = null;
    rotatingOutRef.current = null;
    for (const id of timersRef.current) clearTimeout(id);
    timersRef.current.clear();

    const unregisterTeardown = registerSignOutTeardown?.(async () => {
      signingOutRef.current = true;
      // Await only an in-flight registration settle, then remove every pusher the
      // session registered — the live one plus any pending rotation target, so a
      // sign-out mid-rotation strands neither.
      if (registrationRef.current) await registrationRef.current.catch(() => undefined);
      const current = registeredRef.current;
      const rotatingOut = rotatingOutRef.current;
      registeredRef.current = null;
      rotatingOutRef.current = null;
      if (current) await unregisterIosPusher(client, current);
      if (rotatingOut && rotatingOut.pushkey !== current?.pushkey) {
        await unregisterIosPusher(client, rotatingOut);
      }
    });

    // Foreground / online: detect token rotation or re-arm after exhaustion.
    const onForeground = () => {
      void runCycleRef.current?.();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") onForeground();
    };
    window.addEventListener("online", onForeground);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("online", onForeground);
      document.removeEventListener("visibilitychange", onVisibility);
      // Deregister the sign-out teardown too: a client swap must not leave a stale
      // teardown that would tear down a pusher against the wrong client.
      unregisterTeardown?.();
    };
  }, [client, registerSignOutTeardown]);

  // The cycle is rebuilt each time sign-in state changes; foreground events reach
  // it through a stable ref so the listeners above stay bound for the whole client.
  const runCycleRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    if (!client || !signedIn) return;
    // Re-entering a signed-in state clears a previous session's sign-out flag.
    // MatrixContext keeps the SAME client handle across sign-out → sign-in, so
    // this — not a client change — is what re-arms registration after re-login.
    signingOutRef.current = false;
    // Guards the cycle once this effect instance is cleaned up, so a promise
    // that resolves late cannot reschedule a retry into a timers set nothing
    // will clear anymore.
    let alive = true;

    const validFor = (mine: Client) => !signingOutRef.current && mine === activeClientRef.current;
    // Record the new pusher and hold the replaced one in rotatingOutRef at the
    // same instant, so a sign-out that lands between this and the rotation block
    // still tears down the old pusher instead of stranding it.
    const recordIfLive = (mine: Client, pusher: RegisteredIosPusher) => {
      if (mine !== activeClientRef.current) return;
      rotatingOutRef.current = registeredRef.current;
      registeredRef.current = pusher;
    };

    // One acquisition + registration attempt.
    const attempt = (mine: Client): Promise<AttemptOutcome> =>
      (async (): Promise<AttemptOutcome> => {
        if (!validFor(mine)) return "cancelled";

        let token: string | null;
        try {
          token = await registerApnsToken();
        } catch {
          return "network"; // transient native/APNs failure — retryable
        }
        if (!validFor(mine)) return "cancelled";
        if (token === null) return "denied"; // permission denied: terminal, not retried
        // Already registered with this exact token: no re-register, no rotation
        // (a foreground re-arm with an unchanged token must not delete the live
        // pusher it just re-acquired).
        if (registeredRef.current?.pushkey === token) return "registered";

        // Registration phase — the only thing sign-out may await. Recording the
        // registered pusher (and holding its predecessor) inside the settle keeps
        // it atomic with what the teardown awaits, so no successful late
        // registration survives sign-out.
        const settle = (async (): Promise<{ ok: boolean }> => {
          let ok = false;
          try {
            await mine.bindings.registerPusher(buildIosPusher(token));
            ok = true;
          } catch {
            ok = false;
          }
          if (ok) recordIfLive(mine, { appId: IOS_APP_ID, pushkey: token });
          return { ok };
        })();
        registrationRef.current = settle;
        const { ok } = await settle;
        // Only null the ref if it still holds *this* settle — after a client
        // swap a newer cycle may have set it, and clearing here would erase its
        // pending registration, making the teardown skip the wait and strand it.
        if (registrationRef.current === settle) registrationRef.current = null;
        if (!validFor(mine)) return "cancelled";
        // A token may have resolved after this effect was cleaned up (unmount or
        // same-client re-login); bail so an ended session cannot register.
        if (!alive) return "cancelled";
        if (!validFor(mine)) return "cancelled";
        if (!ok) return "network"; // rejected — retryable; the prior pusher stays

        // Retire the predecessor held in rotatingOutRef now that the replacement
        // is live. Clear it before the async unregister so a sign-out landing
        // mid-teardown cannot double-remove it; the new pusher is torn down by
        // the sign-out teardown, this path retires the old one.
        const retiring = rotatingOutRef.current;
        rotatingOutRef.current = null;
        if (retiring) {
          await unregisterIosPusher(mine, retiring);
        }
        return "registered";
      })();

    // Bounded retry cycle: initial attempt + scheduled delays, coalesced to one
    // operation at a time. A foreground/online event re-arms one cycle.
    const runCycle = () => {
      if (!alive || signingOutRef.current) {
        rearmRequestedRef.current = false;
        return;
      }
      if (inFlightRef.current) {
        rearmRequestedRef.current = true; // coalesce duplicate triggers into one re-arm
        return;
      }
      inFlightRef.current = true;
      let delayIndex = -1;

      const close = () => {
        inFlightRef.current = false;
        if (alive && rearmRequestedRef.current && !signingOutRef.current) {
          rearmRequestedRef.current = false;
          runCycle();
        }
      };

      const step = () => {
        if (!alive || signingOutRef.current) {
          close();
          return;
        }
        void attempt(client)
          .then((outcome) => {
            if (!alive || signingOutRef.current) {
              close();
              return;
            }
            if (outcome === "network") {
              if (delayIndex >= RETRY_DELAYS_MS.length - 1) {
                close(); // exhausted; a foreground/online event re-arms
                return;
              }
              delayIndex += 1;
              const id = setTimeout(step, RETRY_DELAYS_MS[delayIndex]);
              timersRef.current.add(id);
              return;
            }
            close(); // registered, denied or cancelled — terminal for this cycle
          })
          .catch(() => close());
      };

      step();
    };

    runCycleRef.current = runCycle;
    runCycle();

    return () => {
      // Clear pending retries and dispose the cycle without invalidating the
      // session — the teardown owns that, so an in-flight registration is not
      // stranded on re-render.
      alive = false;
      inFlightRef.current = false;
      rearmRequestedRef.current = false;
      for (const id of timersRef.current) clearTimeout(id);
      timersRef.current.clear();
      runCycleRef.current = undefined;
    };
  }, [client, signedIn]);

  // Route notification taps the native layer buffered: on mount (cold start)
  // and on return to foreground (warm tap).
  useEffect(() => {
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const drain = () => {
      void takeLaunchPushTap().then((roomId) => {
        if (!cancelled && roomId) onNotificationTap(roomId);
      });
    };
    drain();
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      // A warm tap's `didReceive` buffers the room id slightly after iOS
      // foregrounds the app, so the immediate drain can miss it — re-drain once.
      // ponytail: replace with a native resumed-lifecycle signal if device
      // testing still drops warm taps.
      drain();
      if (retry !== undefined) clearTimeout(retry);
      retry = setTimeout(drain, 400);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      if (retry !== undefined) clearTimeout(retry);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [onNotificationTap]);
}
